if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    static PARALLAX_STRENGTH = 0.14;

    constructor() {
      super();
      this.onSlideChangedHandler = this.onSlideChanged.bind(this);
      this.onViewerScrollHandler = this.onViewerScroll.bind(this);
      this.onResizeParallaxHandler = this.onResizeParallax.bind(this);
      this.onReducedMotionChangeHandler = this.onReducedMotionChange.bind(this);
      this.parallaxStrength = MediaGallery.PARALLAX_STRENGTH;
      this.syncRaf = null;
      this.parallaxScrollEndTimer = null;
      this.parallaxSettle = false;
      this.slickSlider = null;
      this.slickParallaxRaf = null;
    }

    connectedCallback() {
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');

      if (!this.elements.viewer) return;

      this.elements.viewer.addEventListener('slideChanged', this.onSlideChangedHandler);
      this.onSlickGalleryReadyHandler = this.onSlickGalleryReady.bind(this);
      document.addEventListener('yanaa:slick-gallery-ready', this.onSlickGalleryReadyHandler);
      this.bindParallax();

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          const button = mediaToSwitch.querySelector('button');
          if (button) {
            button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false, true));
          }
        });
      }

      if (this.dataset.desktopLayout === 'thumbnail_slider' && this.mql.matches) {
        this.removeListSemantic();
      }

      this.armDeferredVideos();
    }

    armDeferredVideos() {
      this.querySelectorAll('video.yanaa-deferred-product-video:not([data-armed])').forEach((video) => {
        if (video.dataset.observeBound) return;
        video.dataset.observeBound = 'true';
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          video.querySelectorAll('source[data-src]').forEach((source) => {
            source.src = source.dataset.src;
          });
          if (video.dataset.poster) video.poster = video.dataset.poster;
          video.load();
          video.dataset.armed = 'true';
          observer.disconnect();
        }, { rootMargin: '200px' });
        observer.observe(video);
      });
    }

    disconnectedCallback() {
      this.elements?.viewer?.removeEventListener('slideChanged', this.onSlideChangedHandler);
      document.removeEventListener('yanaa:slick-gallery-ready', this.onSlickGalleryReadyHandler);
      const viewerSlider = this.elements?.viewer?.slider;
      viewerSlider?.removeEventListener('scroll', this.onViewerScrollHandler);
      window.removeEventListener('resize', this.onResizeParallaxHandler);
      this.reducedMotionMql?.removeEventListener('change', this.onReducedMotionChangeHandler);
      if (this.syncRaf) cancelAnimationFrame(this.syncRaf);
      if (this.parallaxScrollEndTimer) clearTimeout(this.parallaxScrollEndTimer);
      this.stopSlickParallaxLoop();
      this.unbindSlickParallax();
      this.clearParallaxTransforms();
    }

    isSlickViewer() {
      return Boolean(this.elements.viewer?.slider?.classList.contains('slick-initialized'));
    }

    onSlickGalleryReady(event) {
      if (!this.parallaxEnabled || !window.jQuery?.fn?.slick) return;
      const sliderEl = event.detail?.slider || this.elements.viewer?.slider;
      if (!sliderEl) return;
      this.bindSlickParallax(window.jQuery, window.jQuery(sliderEl));
    }

    bindParallax() {
      this.reducedMotionMql = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotionMql.addEventListener('change', this.onReducedMotionChangeHandler);
      this.updateParallaxEnabled();

      const viewerSlider = this.elements.viewer?.slider;
      if (viewerSlider) {
        viewerSlider.addEventListener('scroll', this.onViewerScrollHandler, { passive: true });
      }

      window.addEventListener('resize', this.onResizeParallaxHandler, { passive: true });
      this.scheduleParallax();

      if (this.mql.matches && this.isSlickViewer() && window.jQuery?.fn?.slick) {
        this.bindSlickParallax(window.jQuery, window.jQuery(this.elements.viewer.slider));
      }
    }

    onReducedMotionChange() {
      this.updateParallaxEnabled();
      this.scheduleParallax();
    }

    updateParallaxEnabled() {
      this.parallaxEnabled = !this.reducedMotionMql.matches;
      if (this.parallaxEnabled) {
        this.setAttribute('data-parallax', 'on');
      } else {
        this.removeAttribute('data-parallax');
        this.clearParallaxTransforms();
      }
    }

    onResizeParallax() {
      this.scheduleParallax();
    }

    onViewerScroll() {
      this.parallaxSettle = false;
      this.scheduleParallaxFrame();
      this.debounceParallaxSettle();
    }

    debounceParallaxSettle() {
      if (this.isSlickViewer()) return;
      if (this.parallaxScrollEndTimer) clearTimeout(this.parallaxScrollEndTimer);
      this.parallaxScrollEndTimer = setTimeout(() => {
        this.parallaxScrollEndTimer = null;
        this.parallaxSettle = true;
        this.scheduleParallaxFrame();
      }, 120);
    }

    scheduleParallax() {
      this.scheduleParallaxFrame();
    }

    scheduleParallaxFrame() {
      if (!this.parallaxEnabled) return;
      if (this.syncRaf) return;

      this.syncRaf = requestAnimationFrame(() => {
        this.syncRaf = null;
        this.updateParallax();
        this.syncThumbnailsFromScroll();
      });
    }

    syncThumbnailsFromScroll() {
      if (!this.elements.thumbnails) return;

      const currentElement = this.getCurrentViewerSlide();
      if (!currentElement) return;

      const thumbnail = this.elements.thumbnails.querySelector(`[data-target="${currentElement.dataset.mediaId}"]`);
      if (thumbnail) {
        this.setActiveThumbnail(thumbnail, true);
      }
    }

    getParallaxViewport() {
      const slider = this.elements.viewer?.slider;
      if (slider?.classList.contains('slick-initialized')) {
        return slider.querySelector('.slick-list') || slider;
      }
      return slider || this.elements.viewer;
    }

    getParallaxSlides() {
      const viewer = this.elements.viewer;
      if (!viewer) return [];

      return Array.from(viewer.querySelectorAll('.product__media-item[data-media-id]')).filter((slide) => {
        if (getComputedStyle(slide).display === 'none') return false;
        if (slide.clientWidth === 0) return false;
        if (slide.classList.contains('product__media-item--full')) return false;
        if (slide.classList.contains('slick-cloned')) return false;
        if (slide.querySelector('.deferred-media')) return false;
        return Boolean(this.getParallaxTarget(slide));
      });
    }

    getParallaxTarget(slide) {
      const wrapper = slide.querySelector(':scope > .media-wrapper') || slide.querySelector('.media-wrapper');
      if (!wrapper) return slide.querySelector('.product__media');

      return (
        wrapper.querySelector('.product__media img') ||
        wrapper.querySelector('img') ||
        wrapper.querySelector('.product__media')
      );
    }

    setParallaxTransform(target, offsetX, transition) {
      if (!target) return;
      if (offsetX === 0 && !transition) {
        target.style.transform = '';
        target.style.transition = '';
        return;
      }
      target.style.transform = `translate3d(${offsetX}px, 0, 0)`;
      target.style.transition = transition;
    }

    updateParallax() {
      if (!this.parallaxEnabled) {
        this.clearParallaxTransforms();
        return;
      }

      const viewport = this.getParallaxViewport();
      if (!viewport) return;

      const viewportRect = viewport.getBoundingClientRect();
      const viewportCenterX = viewportRect.left + viewportRect.width / 2;
      const useSlick = this.isSlickViewer();
      const transition = useSlick ? '' : this.parallaxSettle ? 'transform 0.2s ease-out' : '';
      const strength = useSlick ? this.parallaxStrength * 2.1 : this.parallaxStrength;

      this.getParallaxSlides().forEach((slide) => {
        const target = this.getParallaxTarget(slide);
        if (!target) return;

        const slideRect = slide.getBoundingClientRect();
        const isNearViewport = slideRect.right > viewportRect.left - 80 && slideRect.left < viewportRect.right + 80;

        if (!isNearViewport) {
          this.setParallaxTransform(target, 0, '');
          return;
        }

        const slideCenterX = slideRect.left + slideRect.width / 2;
        const rawOffsetX = (viewportCenterX - slideCenterX) * strength;
        const maxOffset = Math.min(useSlick ? 72 : 48, slideRect.width * (useSlick ? 0.12 : 0.06));
        const offsetX = Math.max(-maxOffset, Math.min(maxOffset, rawOffsetX));
        this.setParallaxTransform(target, offsetX, transition);
      });
    }

    clearParallaxTransforms() {
      this.getParallaxSlides().forEach((slide) => {
        this.setParallaxTransform(this.getParallaxTarget(slide), 0, '');
      });
    }

    startSlickParallaxLoop() {
      if (this.slickParallaxActive) return;

      this.slickParallaxActive = true;
      const loop = () => {
        if (!this.slickParallaxActive) return;
        if (!this.parallaxEnabled || !this.isSlickViewer()) {
          this.slickParallaxActive = false;
          return;
        }
        this.updateParallax();
        this.slickParallaxRaf = requestAnimationFrame(loop);
      };
      loop();
    }

    stopSlickParallaxLoop() {
      this.slickParallaxActive = false;
      if (this.slickParallaxRaf) {
        cancelAnimationFrame(this.slickParallaxRaf);
        this.slickParallaxRaf = null;
      }
    }

    // A22: the parallax only changes while slides move. Instead of a permanent rAF loop (1,200
    // callbacks per 10 s on an idle desktop page), update once, then run the loop only during a slick
    // transition or a drag, and update once more when it ends.
    bindSlickParallax(_jq, $slider) {
      this.unbindSlickParallax({ clearTransforms: false });
      if (!this.parallaxEnabled || !$slider?.length) return;

      this.slickSlider = $slider;
      this.parallaxSettle = false;
      this.slickParallaxChanging = false;

      const stopAndUpdate = () => {
        this.stopSlickParallaxLoop();
        this.updateParallax();
      };
      $slider.on('beforeChange.yanaaParallax', () => {
        this.slickParallaxChanging = true;
        this.startSlickParallaxLoop();
      });
      $slider.on('afterChange.yanaaParallax', () => {
        this.slickParallaxChanging = false;
        stopAndUpdate();
      });
      $slider.on('setPosition.yanaaParallax', () => {
        if (!this.slickParallaxActive) this.scheduleParallaxFrame();
      });

      const list = $slider.get(0)?.querySelector('.slick-list');
      if (list) {
        this.slickParallaxPointer = new AbortController();
        const { signal } = this.slickParallaxPointer;
        const endDrag = () => {
          if (!this.slickParallaxActive) return;
          clearTimeout(this.slickParallaxDragTimer);
          this.slickParallaxDragTimer = setTimeout(() => {
            if (!this.slickParallaxChanging) stopAndUpdate();
          }, 400);
        };
        list.addEventListener('pointerdown', () => this.startSlickParallaxLoop(), { passive: true, signal });
        // on window: a drag can end outside the slider
        window.addEventListener('pointerup', endDrag, { passive: true, signal });
        window.addEventListener('pointercancel', endDrag, { passive: true, signal });
      }

      this.updateParallax();
    }

    unbindSlickParallax(options = {}) {
      const clearTransforms = options.clearTransforms !== false;
      this.stopSlickParallaxLoop();
      this.slickParallaxPointer?.abort();
      this.slickParallaxPointer = null;
      clearTimeout(this.slickParallaxDragTimer);
      if (this.slickSlider) {
        this.slickSlider.off('.yanaaParallax');
        this.slickSlider = null;
      }
      if (clearTransforms) {
        this.clearParallaxTransforms();
      }
    }

    getCurrentViewerSlide() {
      const viewer = this.elements.viewer;
      if (!viewer?.slider) return null;

      const slides = Array.from(viewer.sliderItems || viewer.querySelectorAll('[id^="Slide-"]')).filter(
        (element) => element.clientWidth > 0 && getComputedStyle(element).display !== 'none'
      );

      if (!slides.length) return null;

      const slider = viewer.slider;
      const scrollLeft = slider.scrollLeft;
      const sliderCenter = scrollLeft + slider.clientWidth / 2;

      let closestSlide = slides[0];
      let closestDistance = Infinity;

      slides.forEach((slide) => {
        const slideCenter = slide.offsetLeft + slide.clientWidth / 2;
        const distance = Math.abs(slideCenter - sliderCenter);
        if (distance < closestDistance) {
          closestDistance = distance;
          closestSlide = slide;
        }
      });

      return closestSlide;
    }

    onSlideChanged(event) {
      if (event.detail.currentElement == undefined) return;

      const thumbnail = this.elements.thumbnails?.querySelector(`[data-target="${event.detail.currentElement.dataset.mediaId}"]`);
      if (thumbnail) {
        this.setActiveThumbnail(thumbnail, true);
      }

      if (!this.isSlickViewer()) {
        this.parallaxSettle = true;
        this.scheduleParallax();
      }
    }

    goToSlickSlide(mediaId) {
      const list = this.elements.viewer?.slider;
      if (!list?.classList.contains('slick-initialized')) return false;

      const jq = window.jQuery;
      if (!jq?.fn?.slick) return false;

      const $slider = jq(list);
      const slides = $slider.find('.slick-slide').not('.slick-cloned');
      let index = -1;

      slides.each(function (i) {
        const slide = this;
        if (
          slide.getAttribute('data-media-id') === mediaId ||
          slide.querySelector(`[data-media-id="${mediaId}"]`)
        ) {
          index = i;
          return false;
        }
      });

      if (index < 0) return false;
      $slider.slick('slickGoTo', index);
      return true;
    }

    setActiveMedia(mediaId, prepend, scrollIntoView = true) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (!this.goToSlickSlide(mediaId)) {
          if (this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          if ((!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') && scrollIntoView) {
            activeMedia.scrollIntoView({ behavior: 'smooth' });
          }
        }
        if (!this.isSlickViewer()) {
          this.parallaxSettle = true;
          this.scheduleParallax();
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail, false);
      this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
    }

    setActiveThumbnail(thumbnail, fromScroll = false) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('[id^="Slide-"]').forEach((element) => {
        element.classList.remove('is-active');
        element.querySelector('button')?.removeAttribute('aria-current');
      });
      thumbnail.classList.add('is-active');
      thumbnail.querySelector('button')?.setAttribute('aria-current', true);

      const slider = this.elements.thumbnails.slider;
      if (!slider) return;

      const targetScroll = thumbnail.offsetLeft - (slider.clientWidth - thumbnail.offsetWidth) / 2;
      const nextScroll = Math.max(0, Math.min(targetScroll, slider.scrollWidth - slider.clientWidth));

      if (fromScroll) {
        slider.scrollLeft = nextScroll;
        return;
      }

      if (!this.elements.thumbnails.isSlideVisible(thumbnail, 10)) {
        slider.scrollTo({ left: nextScroll, behavior: 'smooth' });
      }
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = theme.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
    }
  });
}
